import { Button } from '@edsn/pie-components';
import IconDelete from '@iconify/icons-material-symbols/delete-outline';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '#pie/components/icon/Icon';
import { Dialog } from '#pie/components/layout/dialog/Dialog';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { useToast } from '#pie/components/toast/ToastContext';
import type { TeamDto } from '#pie/internal/api/pie-bff';
import { useDeleteTeamId } from '#pie/internal/api/pie-bff';

interface RemoveTeamButtonnProps {
  data: TeamDto;
}

export const RemoveTeamButton = ({ data }: RemoveTeamButtonnProps) => {
  const { addToast } = useToast();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const { t } = useTranslation();
  const { t: tom } = useTranslation('organisationManagement');
  const queryClient = useQueryClient();

  const { mutate, isPending: isLoading } = useDeleteTeamId({
    mutation: {
      onError: (error) => {
        addToast({
          message: `${tom('error.message.delete_team')}`,
          title: `${tom('error.title.delete_team')}`,
          type: 'error',
          error: error,
        });
      },
      onSuccess: () => {
        addToast({
          message: `${t('common.success.message.delete', { name: data.name })}`,
          title: `${tom('success.title.delete_team')}`,
          type: 'success',
        });
        setOpenDeleteDialog(false);
        queryClient.invalidateQueries({
          predicate: (m) => {
            return m.queryKey.some(
              (n) => typeof n === 'string' && n.includes('/team'),
            );
          },
        });
      },
    },
  });

  return (
    <>
      <Button
        iconStart={<Icon icon={IconDelete} />}
        action="tertiary"
        emphasis="minimal"
        onClick={() => setOpenDeleteDialog(true)}
        className="ml-auto p-0"
        aria-label={tom('button.delete.delete_team')}
      />
      <Dialog
        title={tom('button.delete.delete_team')}
        open={openDeleteDialog}
        onOpenChange={setOpenDeleteDialog}
      >
        <Stack gap="lg">
          <Text>{tom('delete.title_team', { team: data.name })}</Text>
          <div className="grid-auto grid auto-cols-fr grid-flow-col gap-2">
            <Button
              action="tertiary"
              emphasis="subtle"
              onClick={() => {
                setOpenDeleteDialog(false);
              }}
            >
              {tom('button.cancel.keep_team')}
            </Button>
            <Button
              isLoading={isLoading}
              action="danger"
              emphasis="bold"
              onClick={() => mutate({ id: data.id })}
            >
              {tom('button.delete.delete_team')}
            </Button>
          </div>
        </Stack>
      </Dialog>
    </>
  );
};
